/**
 * lazyWithReload — a drop-in replacement for React.lazy() that survives a
 * deploy happening while a tab is open.
 *
 * The problem: our front is deployed often. When a build ships, the CDN serves
 * a NEW index.html with new chunk hashes and PURGES the old chunks. A tab that
 * was loaded before the deploy still holds the OLD index.html in memory, so the
 * first time it lazily imports a route/panel it requests a hash that no longer
 * exists → `Failed to fetch dynamically imported module: …/assets/Foo-abc.js`
 * (404) → the import promise rejects → React's Suspense boundary surfaces the
 * error and the whole surface falls into the ErrorBoundary.
 *
 * The fix: when a dynamic import fails because the chunk is gone, force ONE full
 * page reload. The reload fetches the fresh index.html + fresh chunk hashes and
 * everything resolves. A sessionStorage guard makes it fire AT MOST ONCE per
 * tab-session so a genuinely broken import (chunk really 500s, offline, …) can
 * never turn into a reload loop — the second failure rethrows and the
 * ErrorBoundary takes over as before.
 *
 * `main.tsx` also wires the same guarded reload to Vite's `vite:preloadError`
 * event, which fires for `<link rel="modulepreload">` 404s that never reach a
 * lazy() factory.
 */
import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

/**
 * sessionStorage (not localStorage): the guard must reset when the tab closes,
 * never persist across sessions. One key, shared by every wrapped import and by
 * the preloadError listener, so the "at most one reload" budget is global.
 */
const RELOAD_GUARD_KEY = 'aikka:stale-chunk-reloaded';

function hasReloadedThisSession(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) !== null;
  } catch {
    // sessionStorage can throw (Safari private mode, sandboxed iframe). If we
    // can't read the guard we must assume "already reloaded" so we never risk a
    // loop — a failed import then simply surfaces to the ErrorBoundary.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    /* best-effort: if we can't write, hasReloadedThisSession() returns true */
  }
}

function clearReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Whether an import rejection looks like a purged/missing chunk (as opposed to
 * a real runtime error inside the module). Chrome, Firefox and Safari each word
 * this differently, hence the alternation.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|'?text\/html'?/i.test(
    message,
  );
}

/**
 * Force a single, guarded full reload to pick up the fresh index.html + chunks.
 * Returns true if it triggered the reload, false if the one-reload budget for
 * this tab-session was already spent (caller should then let the error surface).
 * Exported so the `vite:preloadError` listener in main.tsx shares the budget.
 */
export function forceReloadForFreshChunks(): boolean {
  if (hasReloadedThisSession()) {
    return false;
  }
  markReloaded();
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
  return true;
}

/**
 * Wrap a dynamic-import factory the same way React.lazy() does, but recover from
 * a stale-chunk 404 with one guarded reload instead of falling to the
 * ErrorBoundary.
 */
export function lazyWithReload<
  // Mirror React.lazy's own signature: `ComponentType<any>` is what preserves
  // each wrapped component's real prop types through the wrapper (a stricter
  // `unknown` would erase them and break every <Component prop=…/> call site).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>,
>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // A clean import means this tab is now consistent with the deployed
      // build again — release the one-reload budget so a LATER deploy can also
      // recover. (Harmless if the guard was never set.)
      clearReloadGuard();
      return mod;
    } catch (error) {
      if (isChunkLoadError(error) && forceReloadForFreshChunks()) {
        // Reload is in flight. Return a promise that never settles so Suspense
        // keeps the fallback mounted through the navigation rather than
        // flashing the ErrorBoundary for the split second before unload.
        return new Promise<{ default: T }>(() => {});
      }
      // Not a chunk error, or the reload budget is already spent: behave exactly
      // like React.lazy() and let the ErrorBoundary handle it.
      throw error;
    }
  });
}
